import React, { useContext, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiSearch, FiShoppingBag, FiSliders, FiX } from "react-icons/fi";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

const categoryOptions = ["All", "Art Supplies", "Hobby & Creativity", "Pens", "Paper & Pads", "Kids"];
const sortOptions = [
  ["popular", "Most popular"],
  ["rating", "Highest rated"],
  ["newest", "Newest"],
  ["price-low", "Price: low to high"],
  ["price-high", "Price: high to low"],
  ["name", "Name: A–Z"],
];

function Shop() {
  const [params, setParams] = useSearchParams();
  const { addToCart, toggleWishlist, wishlist, openCart } = useContext(CartContext);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(15);
  const search = params.get("search") || "";
  const category = params.get("category") || "All";
  const subcategory = params.get("subcategory") || "";
  const brand = params.get("brand") || "All";
  const sort = params.get("sort") || "popular";
  const price = params.get("price") || "all";
  const wishlistOnly = params.get("wishlist") === "true";
  const newOnly = params.get("new") === "true";

  const brands = useMemo(
    () => ["All", ...Array.from(new Set(productData.map((product) => product.brand))).sort()],
    []
  );

  const subcategories = useMemo(() => {
    const source = category === "All" ? productData : productData.filter((product) => product.category === category);
    return Array.from(new Set(source.map((product) => product.subcategory))).sort();
  }, [category]);

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const searchable = `${product.brand} ${product.name} ${product.category} ${product.subcategory} ${product.tags.join(" ")}`.toLowerCase();
      return (
        (!term || searchable.includes(term)) &&
        (category === "All" || product.category === category) &&
        (!subcategory || product.subcategory === subcategory) &&
        (brand === "All" || product.brand === brand) &&
        (!newOnly || product.isNew) &&
        (!wishlistOnly || wishlist.includes(product.id)) &&
        (price === "all" ||
          (price === "45-80" && product.price >= 45 && product.price <= 80) ||
          (price === "81-120" && product.price >= 81 && product.price <= 120) ||
          (price === "121-180" && product.price >= 121 && product.price <= 180))
      );
    });

    return [...filtered].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "newest") return Number(Boolean(b.isNew)) - Number(Boolean(a.isNew)) || b.id - a.id;
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      return Number(Boolean(b.featured)) - Number(Boolean(a.featured)) || b.reviews - a.reviews;
    });
  }, [search, category, subcategory, brand, newOnly, wishlistOnly, wishlist, price, sort]);

  const setParam = (key, value, defaultValue = "") => {
    const next = new URLSearchParams(params);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    if (key === "category") next.delete("subcategory");
    setVisibleCount(15);
    setParams(next);
  };

  const clearFilters = () => {
    setParams(new URLSearchParams());
    setVisibleCount(15);
  };

  const pageTitle = wishlistOnly
    ? "Your wishlist"
    : search
      ? `Results for “${search}”`
      : newOnly
        ? "New arrivals"
        : subcategory || (category !== "All" ? category : "Shop all");

  const addProduct = (product) => {
    addToCart(product.id, 1, product.variants[0], product.colors[0]);
    openCart();
  };

  return (
    <main className="shop-page">
      <nav className="shop-page__breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link><span>→</span><span aria-current="page">{pageTitle}</span>
      </nav>

      <header className="shop-page__header">
        <h1>{pageTitle}</h1>
        <p>
          {search
            ? `Browse products matching “${search}”, then refine the result by category, maker or price.`
            : "Tools selected for clear colour, dependable performance and the particular pleasure of making something by hand."}
        </p>
      </header>

      {!search && category === "All" && !wishlistOnly && !newOnly && (
        <div className="shop-page__category-chips" aria-label="Product categories">
          {categoryOptions.slice(1).map((option) => (
            <button type="button" key={option} onClick={() => setParam("category", option, "All")}>{option}</button>
          ))}
        </div>
      )}

      <section className="shop-page__toolbar" aria-label="Product filters and sorting">
        <button type="button" className="shop-page__mobile-filter" onClick={() => setMobileFiltersOpen(true)}>
          <FiSliders /> Filters
        </button>
        <div className={`shop-page__filters ${mobileFiltersOpen ? "is-open" : ""}`}>
          <div className="shop-page__filters-title">
            <strong>Filters</strong>
            <button type="button" aria-label="Close filters" onClick={() => setMobileFiltersOpen(false)}><FiX /></button>
          </div>
          <label>
            <span>Category</span>
            <select value={category} onChange={(event) => setParam("category", event.target.value, "All")}>
              {categoryOptions.map((option) => <option key={option}>{option}</option>)}
            </select>
            <FiChevronDown />
          </label>
          <label>
            <span>Type</span>
            <select value={subcategory} onChange={(event) => setParam("subcategory", event.target.value)}>
              <option value="">All types</option>
              {subcategories.map((option) => <option key={option}>{option}</option>)}
            </select>
            <FiChevronDown />
          </label>
          <label>
            <span>Brand</span>
            <select value={brand} onChange={(event) => setParam("brand", event.target.value, "All")}>
              {brands.map((option) => <option key={option}>{option}</option>)}
            </select>
            <FiChevronDown />
          </label>
          <label>
            <span>Price</span>
            <select value={price} onChange={(event) => setParam("price", event.target.value, "all")}>
              <option value="all">All prices</option>
              <option value="45-80">$45–$80</option>
              <option value="81-120">$81–$120</option>
              <option value="121-180">$121–$180</option>
            </select>
            <FiChevronDown />
          </label>
          <button type="button" className="shop-page__clear" onClick={clearFilters}>Clear filters</button>
          <button type="button" className="shop-page__apply" onClick={() => setMobileFiltersOpen(false)}>Show {results.length} products</button>
        </div>

        <span className="shop-page__count">{results.length} products</span>
        <label className="shop-page__sort">
          <span>Sort by</span>
          <select value={sort} onChange={(event) => setParam("sort", event.target.value, "popular")}>
            {sortOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
          <FiChevronDown />
        </label>
      </section>

      {results.length > 0 ? (
        <>
          <section className="shop-page__grid" aria-label="Products">
            {results.slice(0, visibleCount).map((product) => {
              const wished = wishlist.includes(product.id);
              return (
                <article className="shop-card" key={product.id}>
                  <Link to={`/shopDetails/${product.id}`} className="shop-card__image">
                    {product.isNew && <span>New</span>}
                    <img src={product.image} alt={product.name} loading="lazy" width="1254" height="1254" />
                    <small>{product.colors.length} {product.colors.length === 1 ? "colour" : "colours"}</small>
                  </Link>
                  <button
                    type="button"
                    className={`shop-card__wish ${wished ? "is-active" : ""}`}
                    onClick={() => toggleWishlist(product.id)}
                    aria-label={`${wished ? "Remove" : "Add"} ${product.name} ${wished ? "from" : "to"} wishlist`}
                    aria-pressed={wished}
                  >
                    <FiHeart />
                  </button>
                  <div className="shop-card__details">
                    <span>{product.brand}</span>
                    <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                    <div><strong>{formatPrice(product.price)}</strong><small>★ {product.rating}</small></div>
                    <p><i /> In stock</p>
                  </div>
                  <button type="button" className="shop-card__add" onClick={() => addProduct(product)}><FiShoppingBag /> Quick add</button>
                </article>
              );
            })}
          </section>
          {visibleCount < results.length && (
            <div className="shop-page__more">
              <p>Showing {visibleCount} of {results.length}</p>
              <button type="button" onClick={() => setVisibleCount((count) => count + 10)}>Show more</button>
            </div>
          )}
        </>
      ) : (
        <section className="shop-page__empty" aria-live="polite">
          <FiSearch />
          <h2>No creative tools found</h2>
          <p>Try a broader search, remove a filter, or browse the full {siteConfig.brandName} collection.</p>
          <button type="button" onClick={clearFilters}>View all products</button>
        </section>
      )}

      {mobileFiltersOpen && <button type="button" className="shop-page__scrim" aria-label="Close filters" onClick={() => setMobileFiltersOpen(false)} />}
    </main>
  );
}

export default Shop;
